import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Upload, Save, ShieldCheck, X as XIcon, CheckCircle2, Trash2, Plus, Info, ZapOff, Zap } from "lucide-react";

/**
 * QR Manager — Adiyogi Handicrafts
 * -----------------------------------------------------------------------------
 * Admin uploads a new UPI/QR configuration; the previous active one is
 * automatically deactivated. Customers always scan a DYNAMIC svg QR built from
 * the currently active UPI ID + this order's exact amount, so the amount is
 * always pre-filled regardless of how many times the QR is rotated.
 * The uploaded image is stored as a reference/brand asset only.
 */
export default function AdminPaymentSettings() {
  const [loading, setLoading] = useState(true);
  const [qrs, setQrs] = useState([]);
  const [flags, setFlags] = useState({ is_upi_enabled: true });
  const [savingFlag, setSavingFlag] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: settings }] = await Promise.all([
        api.get("/admin/payment-qrs"),
        api.get("/payment-settings"),
      ]);
      setQrs(list || []);
      setFlags({ is_upi_enabled: settings.is_upi_enabled !== false });
    } catch (e) {
      toast.error("Could not load payment settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const active = qrs.find((q) => q.is_active) || null;
  const inactive = qrs.filter((q) => !q.is_active);

  const toggleEnabled = async () => {
    const next = !flags.is_upi_enabled;
    setSavingFlag(true);
    try {
      await api.patch("/admin/payment-settings", { is_upi_enabled: next });
      setFlags({ is_upi_enabled: next });
      toast.success(next ? "UPI checkout enabled" : "UPI checkout disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingFlag(false); }
  };

  const activate = async (qr) => {
    if (qr.is_active) return;
    try {
      await api.post(`/admin/payment-qrs/${qr.qr_id}/activate`);
      toast.success(`Activated: ${qr.label || qr.upi_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Activation failed"); }
  };

  const remove = async (qr) => {
    if (qr.is_active) { toast.error("Cannot delete the active QR. Activate another first."); return; }
    if (!window.confirm(`Delete QR "${qr.label || qr.upi_id}"?`)) return;
    try {
      await api.delete(`/admin/payment-qrs/${qr.qr_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const upiSample = active?.upi_id || "yourupi@bank";
  const upiName = active?.upi_name || "AdiYogi Handcrafts";
  const sampleAmount = 1000;
  const previewLink = `upi://pay?pa=${encodeURIComponent(upiSample)}&pn=${encodeURIComponent(upiName)}&am=${sampleAmount}&cu=INR&tn=SAMPLE`;

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-payment-settings">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl">Payment · QR Manager</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Upload as many UPI configurations as you like. The one marked <b>Active</b> is what customers pay to.
            Customers always scan a <b>dynamic QR</b> that auto-fills the exact order amount — no matter how often you rotate the active QR.
          </p>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={savingFlag}
          className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 ${flags.is_upi_enabled ? "btn-outline-gold" : "bg-destructive text-white"}`}
          data-testid="toggle-upi-enabled"
        >
          {flags.is_upi_enabled ? <Zap className="w-3.5 h-3.5" strokeWidth={1.5} /> : <ZapOff className="w-3.5 h-3.5" strokeWidth={1.5} />}
          {flags.is_upi_enabled ? "Checkout: UPI Enabled" : "Checkout: UPI Disabled"}
        </button>
      </div>

      {/* How it works */}
      <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-4 text-sm flex gap-3" data-testid="how-it-works">
        <Info className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="text-emerald-900">
          <div className="font-medium mb-1">How the dynamic amount works</div>
          <ul className="list-disc pl-4 text-[13px] space-y-0.5">
            <li>Customer sees a QR that is <b>regenerated for every order</b> using the Active UPI ID and their exact cart total.</li>
            <li>Scanning it (or tapping “Open with UPI App”) prefills the amount in PhonePe / GPay / Paytm / BHIM automatically.</li>
            <li>Changing the Active QR here <b>never</b> breaks the amount prefill — the new UPI ID takes effect on the next order load.</li>
            <li>The uploaded QR image is stored only as a brand/reference asset — it is <b>not</b> what the customer scans.</li>
          </ul>
        </div>
      </div>

      {/* Active QR */}
      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <div className="border-2 border-[#B8860B] rounded-xl bg-white p-4 md:p-6" data-testid="active-qr-card">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <div className="text-xs uppercase tracking-widest gold-text">Currently Active</div>
          </div>
          {active ? (
            <div className="grid sm:grid-cols-[140px_1fr] gap-4 items-start">
              <div className="w-32 h-32 border hairline rounded-lg overflow-hidden bg-muted/30 flex items-center justify-center shrink-0">
                {active.qr_image_url ? (
                  <img src={active.qr_image_url} alt="Uploaded QR" className="w-full h-full object-contain" data-testid="active-qr-image" />
                ) : (
                  <div className="text-[10px] text-muted-foreground text-center px-2">No uploaded QR<br/>(dynamic QR still works)</div>
                )}
              </div>
              <div className="text-sm space-y-1">
                <div className="font-serif text-lg gold-text break-all" data-testid="active-upi-id">{active.upi_id}</div>
                <div className="text-muted-foreground">{active.upi_name}</div>
                {active.label && <div className="text-xs text-muted-foreground">{active.label}</div>}
                <div className="text-[11px] text-muted-foreground">Activated: {active.activated_at ? new Date(active.activated_at).toLocaleString("en-IN") : "—"}</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No active UPI configuration. Add one below.</div>
          )}
        </div>

        {/* Customer preview */}
        <div className="border hairline rounded-xl bg-white p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <div className="font-serif text-lg">Customer Preview</div>
          </div>
          <div className="text-[11px] text-muted-foreground mb-3">Sample order of ₹{sampleAmount}</div>
          <div className="flex justify-center">
            <div className="bg-white p-3 border hairline rounded-lg">
              {active?.upi_id ? (
                <QRCodeSVG value={previewLink} size={160} level="M" includeMargin={false} fgColor="#2C241E" bgColor="#FDFBF7" />
              ) : (
                <div className="w-40 h-40 rounded bg-muted flex items-center justify-center text-[10px] text-muted-foreground text-center px-2">Add an Active UPI<br/>to preview</div>
              )}
            </div>
          </div>
          <div className="mt-3 text-[11px] text-center text-muted-foreground">Amount ₹{sampleAmount} is auto-filled when scanned.</div>
        </div>
      </div>

      {/* Add new QR */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="font-serif text-2xl">All UPI Configurations</h2>
          <button onClick={() => setShowAdd((v) => !v)} className="btn-gold rounded-full px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="new-qr-btn">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {showAdd ? "Cancel" : "Add & Activate New QR"}
          </button>
        </div>
        {showAdd && <AddQrForm onDone={() => { setShowAdd(false); load(); }} />}
      </div>

      {/* Inactive list */}
      {inactive.length > 0 && (
        <div className="border hairline rounded-xl bg-white overflow-hidden" data-testid="inactive-qr-list">
          <div className="px-4 py-3 border-b hairline text-xs uppercase tracking-widest text-muted-foreground">Inactive ({inactive.length})</div>
          <ul className="divide-y hairline">
            {inactive.map((q) => (
              <li key={q.qr_id} className="p-3 md:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`qr-row-${q.qr_id}`}>
                <div className="w-16 h-16 rounded border hairline overflow-hidden bg-muted/30 shrink-0 flex items-center justify-center">
                  {q.qr_image_url ? <img src={q.qr_image_url} alt="QR" className="w-full h-full object-contain" /> : <div className="text-[9px] text-muted-foreground">No image</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{q.label || q.upi_id}</div>
                  <div className="text-xs text-muted-foreground break-all">{q.upi_id} · {q.upi_name}</div>
                  <div className="text-[10px] text-muted-foreground">Added {q.created_at ? new Date(q.created_at).toLocaleDateString("en-IN") : "—"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => activate(q)} className="btn-gold rounded-full px-4 py-1.5 text-[11px] uppercase tracking-widest" data-testid={`activate-${q.qr_id}`}>Activate</button>
                  <button onClick={() => remove(q)} className="text-destructive text-xs inline-flex items-center gap-1" data-testid={`delete-${q.qr_id}`}>
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function AddQrForm({ onDone }) {
  const [form, setForm] = useState({ label: "", upi_id: "", upi_name: "AdiYogi Handcrafts", qr_image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const upiValid = /^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z]{2,}$/.test((form.upi_id || "").trim());

  const uploadQr = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post("/admin/bulk-upload/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = (data.files || []).find((f) => f.url)?.url;
      if (!url) { toast.error("Upload failed"); return; }
      setForm((f) => ({ ...f, qr_image_url: url }));
      toast.success("QR image uploaded");
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!upiValid) { toast.error("Enter a valid UPI ID (e.g. name@upi)"); return; }
    setSaving(true);
    try {
      await api.post("/admin/payment-qrs", form);
      toast.success("New QR activated. Previous QR is now inactive.");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border hairline rounded-xl bg-white p-4 md:p-6 space-y-4" data-testid="add-qr-form">
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs text-muted-foreground mb-1">UPI ID *</div>
          <input
            type="text"
            value={form.upi_id}
            onChange={(e) => setForm({ ...form, upi_id: e.target.value.trim() })}
            placeholder="yourname@upi"
            className={`w-full border rounded-lg px-3 py-2.5 text-sm font-mono outline-none focus:border-[#B8860B] ${form.upi_id && !upiValid ? "border-destructive" : "hairline"}`}
            data-testid="new-qr-upi-id"
          />
        </label>
        <label className="block">
          <div className="text-xs text-muted-foreground mb-1">Payee Name</div>
          <input
            type="text"
            value={form.upi_name}
            onChange={(e) => setForm({ ...form, upi_name: e.target.value })}
            className="w-full border hairline rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#B8860B]"
            data-testid="new-qr-upi-name"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="text-xs text-muted-foreground mb-1">Label (optional)</div>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="e.g. HDFC Bank · Sept"
            className="w-full border hairline rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#B8860B]"
            data-testid="new-qr-label"
          />
        </label>
      </div>

      <div>
        <div className="text-xs text-muted-foreground mb-1">QR Image (optional — stored as reference only)</div>
        <div className="flex items-start gap-3 flex-wrap">
          {form.qr_image_url && (
            <div className="relative w-24 h-24 border hairline rounded-lg overflow-hidden bg-white">
              <img src={form.qr_image_url} alt="QR" className="w-full h-full object-contain" />
              <button type="button" onClick={() => setForm((f) => ({ ...f, qr_image_url: "" }))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center">
                <XIcon className="w-3 h-3" strokeWidth={2} />
              </button>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadQr(e.target.files)} data-testid="new-qr-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline-gold rounded-full px-4 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="new-qr-upload-btn">
              <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> {uploading ? "Uploading…" : (form.qr_image_url ? "Replace Image" : "Upload QR Image")}
            </button>
            <div className="text-[10px] text-muted-foreground max-w-[280px]">Just for your records. The customer scans a dynamic QR built from the UPI ID above so the amount is always correct.</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <button
          onClick={submit}
          disabled={!upiValid || saving}
          className={`rounded-full px-6 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 ${upiValid ? "btn-gold" : "border hairline text-muted-foreground opacity-60"}`}
          data-testid="submit-new-qr"
        >
          <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
          {saving ? "Saving…" : "Save & Activate"}
        </button>
        <div className="text-[11px] text-muted-foreground">Saving will deactivate the previous QR automatically.</div>
      </div>
    </div>
  );
}
